// Create and edit an invoice.
//
// The draft lives entirely in component state until it is saved; the legacy app
// staged lines in a `tmp` database table keyed by PHP session, which meant two
// tabs shared one draft and an abandoned draft left rows behind. Saving sends
// the whole invoice in one request, and the server recomputes the totals.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { fieldErrors, useAsync, useErrorMessage } from '../lib/hooks';
import { useI18n, useT } from '../lib/i18n';
import { useSession } from '../lib/session';
import { useToast } from '../components/Toast';
import { Icon } from '../components/Icon';
import { ClientCombobox } from '../components/ClientCombobox';
import { ClientForm } from '../components/ClientForm';
import { ProductForm } from '../components/ProductForm';
import { ProductPicker, type PickedLine } from '../components/ProductPicker';
import { EmailDialog } from '../components/EmailDialog';
import {
  EmptyState, ErrorAlert, LoadingRow, Modal, SelectField, TextAreaField, TextField,
} from '../components/ui';
import { formatMoney, lineTotal, roundMoney, toDateInput } from '@shared/format';
import {
  PAYMENT_METHODS, type Client, type InvoiceInput, type InvoiceStatus,
  type PaymentMethod, type User,
} from '@shared/types';

/**
 * A line in the draft. Not an extension of PickedLine: a line loaded from an
 * existing invoice can have a null productId (the product was deleted since),
 * whereas one chosen from the picker always has one.
 */
interface DraftLine {
  /** Stable key for React, since a draft line has no database id yet. */
  key: string;
  productId: number | null;
  code: string;
  description: string;
  quantity: number;
  unitPrice: number;
}

let lineCounter = 0;
const nextKey = () => `line-${++lineCounter}`;

export function InvoiceEditor() {
  const params = useParams<{ id: string }>();
  const isNew = params.id === 'new';
  const invoiceId = isNew ? undefined : Number(params.id);

  const t = useT();
  const { locale } = useI18n();
  const { money, taxRate: settingsTaxRate } = useSession();
  const toast = useToast();
  const navigate = useNavigate();
  const toMessage = useErrorMessage();

  // ── draft state ───────────────────────────────────────────────────────────
  const [client, setClient] = useState<Client>();
  const [sellerId, setSellerId] = useState<number | ''>('');
  const [issuedAt, setIssuedAt] = useState(toDateInput(new Date()));
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('cash');
  const [status, setStatus] = useState<InvoiceStatus>('pending');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [taxRate, setTaxRate] = useState(settingsTaxRate);
  const [invoiceNumber, setInvoiceNumber] = useState<number>();
  const [currencySymbol, setCurrencySymbol] = useState(money.symbol);

  const [modal, setModal] = useState<'products' | 'client' | 'product' | 'email' | undefined>();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const sellers = useAsync(() => api.activeUsers(), []);

  // ── load ──────────────────────────────────────────────────────────────────
  const loadExisting = useCallback(async () => {
    if (invoiceId === undefined) {
      const defaults = await api.invoiceDefaults();
      setIssuedAt(defaults.issuedAt);
      setTaxRate(defaults.taxRate);
      setCurrencySymbol(defaults.currency.symbol);
      if (defaults.sellerId !== null) setSellerId(defaults.sellerId);
      return undefined;
    }
    const invoice = await api.invoice(invoiceId);
    setClient({
      id: invoice.clientId, name: invoice.clientName, taxId: invoice.clientTaxId,
      phone: invoice.clientPhone, email: invoice.clientEmail, address: invoice.clientAddress,
      isActive: true, createdAt: invoice.issuedAt,
    });
    setSellerId(invoice.sellerId);
    setIssuedAt(toDateInput(invoice.issuedAt));
    setPaymentMethod(invoice.paymentMethod);
    setStatus(invoice.status);
    setNotes(invoice.notes);
    setTaxRate(invoice.taxRate);
    setInvoiceNumber(invoice.number);
    setCurrencySymbol(invoice.currencySymbol);
    setLines(invoice.lines.map((line) => ({
      key: nextKey(),
      productId: line.productId,
      code: line.code,
      description: line.description,
      quantity: line.quantity,
      unitPrice: line.unitPrice,
    })));
    setDirty(false);
    return invoice;
  }, [invoiceId]);

  const { loading, error, reload } = useAsync(loadExisting, [invoiceId]);

  // Warn before losing an unsaved draft to a browser navigation.
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => { event.preventDefault(); };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  // ── line editing ──────────────────────────────────────────────────────────
  const touch = () => setDirty(true);

  const addLine = (line: PickedLine) => {
    setLines((prev) => {
      // Adding the same product again bumps the quantity rather than repeating
      // the row, which is what the old picker did and read badly on the PDF.
      const index = prev.findIndex(
        (l) => l.productId === line.productId && l.unitPrice === line.unitPrice);
      const existing = index >= 0 ? prev[index] : undefined;
      if (existing) {
        const next = [...prev];
        next[index] = { ...existing, quantity: existing.quantity + line.quantity };
        return next;
      }
      return [...prev, { ...line, key: nextKey() }];
    });
    touch();
    toast.success(t('invoice.picker.added', { name: line.description }));
  };

  const updateLine = (key: string, patch: Partial<DraftLine>) => {
    setLines((prev) => prev.map((line) => (line.key === key ? { ...line, ...patch } : line)));
    touch();
  };

  const removeLine = (key: string) => {
    setLines((prev) => prev.filter((line) => line.key !== key));
    touch();
  };

  // ── totals (mirrors the server's arithmetic) ──────────────────────────────
  const totals = useMemo(() => {
    const subtotal = roundMoney(lines.reduce((sum, l) => sum + lineTotal(l.quantity, l.unitPrice), 0));
    const tax = roundMoney((subtotal * taxRate) / 100);
    return { subtotal, tax, total: roundMoney(subtotal + tax) };
  }, [lines, taxRate]);

  const currency = (value: number) => formatMoney(value, { ...money, symbol: currencySymbol });

  // ── save ──────────────────────────────────────────────────────────────────
  async function save() {
    setErrors({});
    setFormError(undefined);

    const localErrors: Record<string, string> = {};
    if (!client) localErrors.clientId = t('error.invoiceClientRequired');
    if (sellerId === '') localErrors.sellerId = t('error.required');
    if (lines.length === 0) localErrors.lines = t('error.invoiceNoLines');
    if (Object.keys(localErrors).length > 0) {
      setErrors(localErrors);
      if (localErrors.lines) setFormError(localErrors.lines);
      return;
    }

    const payload: InvoiceInput = {
      clientId: client!.id,
      sellerId: Number(sellerId),
      issuedAt,
      paymentMethod,
      status,
      notes,
      lines: lines.map((line) => ({
        productId: line.productId,
        code: line.code,
        description: line.description,
        quantity: line.quantity,
        unitPrice: line.unitPrice,
      })),
    };

    setSaving(true);
    try {
      const saved = invoiceId === undefined
        ? await api.createInvoice(payload)
        : await api.updateInvoice(invoiceId, payload);
      setDirty(false);
      toast.success(t(invoiceId === undefined ? 'invoice.created' : 'invoice.updated',
        { number: saved.number }));
      navigate(`/invoices/${saved.id}`, { replace: true });
      if (invoiceId !== undefined) reload();
    } catch (err) {
      const fields = fieldErrors(err);
      // zod reports line problems as "lines.0.quantity"; surface them as one
      // message rather than trying to anchor each to a row.
      setErrors(fields);
      setFormError(toMessage(err));
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <LoadingRow />;
  if (error) return <ErrorAlert message={error} onRetry={reload} />;

  const sellerOptions: User[] = sellers.data ?? [];

  return (
    <>
      <div className="page-head">
        <div>
          <div className="row row--sm" style={{ gap: 10 }}>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => navigate('/invoices')}>
              <Icon name="arrow-left" size={14} />
              {t('invoices.title')}
            </button>
          </div>
          <h1 className="page-head__title" style={{ marginTop: 6 }}>
            {invoiceNumber !== undefined
              ? t('invoice.edit.title', { number: invoiceNumber })
              : t('invoice.new.title')}
          </h1>
        </div>
        <div className="page-head__actions">
          {invoiceId !== undefined && (
            <>
              <button type="button" className="btn" onClick={() => setModal('email')}>
                <Icon name="mail" />
                {t('email.send')}
              </button>
              <a className="btn" href={api.invoicePdfUrl(invoiceId, locale)}>
                <Icon name="download" />
                {t('invoices.download')}
              </a>
            </>
          )}
          <button type="button" className="btn btn--primary" onClick={() => void save()} disabled={saving}>
            {saving ? <span className="spinner" /> : <Icon name="save" />}
            {saving ? t('common.saving') : t('invoice.save')}
          </button>
        </div>
      </div>

      {formError && <div style={{ marginBottom: 14 }}><ErrorAlert message={formError} /></div>}

      <div className="editor-grid">
        <div className="stack">
          {/* ── header fields ── */}
          <div className="card">
            <div className="card__body">
              <div className="form-grid form-grid--2">
                <div className="field span-all">
                  <label className="field__label">{t('invoice.field.client')}</label>
                  <ClientCombobox
                    value={client}
                    error={errors.clientId}
                    onSelect={(next) => { setClient(next); touch(); }}
                  />
                  {errors.clientId
                    ? <div className="field__error">{errors.clientId}</div>
                    : client && (
                      <div className="field__hint">
                        {[client.taxId, client.phone, client.email].filter(Boolean).join(' · ') || ' '}
                      </div>
                    )}
                </div>

                <SelectField
                  label={t('invoice.field.seller')}
                  value={sellerId}
                  error={errors.sellerId}
                  onChange={(e) => { setSellerId(Number(e.target.value)); touch(); }}
                >
                  <option value="" disabled>—</option>
                  {sellerOptions.map((user) => (
                    <option key={user.id} value={user.id}>{user.firstName} {user.lastName}</option>
                  ))}
                </SelectField>

                <TextField
                  label={t('invoice.field.date')}
                  type="date"
                  value={issuedAt}
                  error={errors.issuedAt}
                  onChange={(e) => { setIssuedAt(e.target.value); touch(); }}
                />

                <SelectField
                  label={t('invoice.field.paymentMethod')}
                  value={paymentMethod}
                  onChange={(e) => { setPaymentMethod(e.target.value as PaymentMethod); touch(); }}
                >
                  {PAYMENT_METHODS.map((method) => (
                    <option key={method} value={method}>{t(`payment.${method}`)}</option>
                  ))}
                </SelectField>

                <SelectField
                  label={t('invoice.field.status')}
                  value={status}
                  onChange={(e) => { setStatus(e.target.value as InvoiceStatus); touch(); }}
                >
                  <option value="pending">{t('status.pending')}</option>
                  <option value="paid">{t('status.paid')}</option>
                </SelectField>
              </div>
            </div>
          </div>

          {/* ── lines ── */}
          <div className="card">
            <div className="card__head">
              <h2 className="card__title">{t('invoice.addLines')}</h2>
              <div className="btn-group">
                <button type="button" className="btn btn--sm" onClick={() => setModal('client')}>
                  <Icon name="users" size={14} />{t('invoice.newClient')}
                </button>
                <button type="button" className="btn btn--sm" onClick={() => setModal('product')}>
                  <Icon name="box" size={14} />{t('invoice.newProduct')}
                </button>
                <button type="button" className="btn btn--primary btn--sm" onClick={() => setModal('products')}>
                  <Icon name="plus" size={14} />{t('invoice.addLines')}
                </button>
              </div>
            </div>

            {lines.length === 0 ? (
              <EmptyState
                icon="box"
                title={t('invoice.lines.empty')}
                hint={t('invoice.lines.emptyHint')}
                action={
                  <button type="button" className="btn btn--primary" onClick={() => setModal('products')}>
                    <Icon name="plus" />{t('invoice.addLines')}
                  </button>
                }
              />
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th className="shrink">{t('invoice.lines.code')}</th>
                      <th>{t('invoice.lines.description')}</th>
                      <th className="shrink num">{t('invoice.lines.quantity')}</th>
                      <th className="shrink num">{t('invoice.lines.unitPrice')}</th>
                      <th className="num">{t('invoice.lines.lineTotal')}</th>
                      <th className="shrink" />
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((line) => (
                      <tr key={line.key}>
                        <td className="shrink mono">{line.code || '—'}</td>
                        <td>
                          <input
                            className="input"
                            value={line.description}
                            aria-label={t('invoice.lines.description')}
                            onChange={(e) => updateLine(line.key, { description: e.target.value })}
                          />
                        </td>
                        <td className="shrink">
                          <input
                            className="input input--numeric line-qty"
                            inputMode="decimal"
                            aria-label={t('invoice.lines.quantity')}
                            value={line.quantity}
                            onChange={(e) => updateLine(line.key,
                              { quantity: Number(e.target.value.replace(',', '.')) || 0 })}
                          />
                        </td>
                        <td className="shrink">
                          <input
                            className="input input--numeric line-price"
                            inputMode="decimal"
                            aria-label={t('invoice.lines.unitPrice')}
                            value={line.unitPrice}
                            onChange={(e) => updateLine(line.key,
                              { unitPrice: Number(e.target.value.replace(',', '.')) || 0 })}
                          />
                        </td>
                        <td className="num table__primary">
                          {currency(lineTotal(line.quantity, line.unitPrice))}
                        </td>
                        <td className="shrink">
                          <button
                            type="button"
                            className="btn btn--ghost btn--icon"
                            onClick={() => removeLine(line.key)}
                            title={t('invoice.lines.remove')}
                            aria-label={t('invoice.lines.remove')}
                          >
                            <Icon name="trash" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* ── side panel ── */}
        <aside className="editor-side">
          <div className="card">
            <div className="card__head"><h2 className="card__title">{t('invoice.total')}</h2></div>
            <div className="card__body">
              <div className="totals">
                <div className="totals__row">
                  <span className="totals__label">{t('invoice.subtotal')}</span>
                  <span className="totals__value">{currency(totals.subtotal)}</span>
                </div>
                <div className="totals__row">
                  <span className="totals__label">{t('invoice.tax', { rate: taxRate })}</span>
                  <span className="totals__value">{currency(totals.tax)}</span>
                </div>
                <div className="totals__row totals__row--grand">
                  <span className="totals__label">{t('invoice.total')}</span>
                  <span className="totals__value">{currency(totals.total)}</span>
                </div>
              </div>
              <p className="field__hint" style={{ marginTop: 12 }}>{t('invoice.taxRateNote')}</p>
            </div>
          </div>

          <div className="card">
            <div className="card__body">
              <TextAreaField
                label={t('invoice.field.notes')}
                optional
                rows={4}
                placeholder={t('invoice.field.notesPlaceholder')}
                value={notes}
                onChange={(e) => { setNotes(e.target.value); touch(); }}
              />
            </div>
          </div>
        </aside>
      </div>

      {/* ── modals ── */}
      {modal === 'products' && (
        <Modal title={t('invoice.picker.title')} wide onClose={() => setModal(undefined)}
          footer={
            <button type="button" className="btn btn--primary" onClick={() => setModal(undefined)}>
              {t('common.close')}
            </button>
          }
        >
          <ProductPicker onAdd={addLine} />
        </Modal>
      )}

      {modal === 'client' && (
        <Modal title={t('clients.new')} onClose={() => setModal(undefined)}>
          <ClientForm
            onCancel={() => setModal(undefined)}
            onSaved={(saved) => {
              setClient(saved);
              setModal(undefined);
              touch();
              toast.success(t('clients.created'));
            }}
          />
        </Modal>
      )}

      {modal === 'email' && invoiceId !== undefined && (
        <EmailDialog
          kind="invoice"
          id={invoiceId}
          documentLabel={`${t('invoices.title')} ${invoiceNumber} · ${client?.name ?? ''}`}
          onClose={() => setModal(undefined)}
          onSent={reload}
        />
      )}

      {modal === 'product' && (
        <Modal title={t('products.new')} onClose={() => setModal(undefined)}>
          <ProductForm
            onCancel={() => setModal(undefined)}
            onSaved={(saved) => {
              // Put the new product straight onto the invoice — creating one
              // mid-invoice almost always means it belongs on this invoice.
              addLine({
                productId: saved.id, code: saved.code, description: saved.name,
                quantity: 1, unitPrice: saved.unitPrice,
              });
              setModal(undefined);
            }}
          />
        </Modal>
      )}
    </>
  );
}
